/* CronHealthWidget — single pane of glass over every daily cron job
 * (ops digest, low-stock, security access changes, catalog image purge).
 *
 * Mounted on the Admin Overview tab. Pulls /api/academy/admin/cron/health
 * which is computed from `digest_log` + the DIGEST_HOUR/TZ schedule env.
 *
 * Auth: cookie JWT (same as the rest of /admin). Component just calls fetch
 * with credentials:'include' — works for super-admin and division admins.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, CheckCircle2, AlertTriangle, Clock, RefreshCw, Mail,
  Bell, ShieldCheck, Boxes, BarChart3, ChevronRight,
} from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const ICON_BY_ID = {
  ops_digest:      BarChart3,
  low_stock:       Bell,
  security_access: ShieldCheck,
  image_purge:     Boxes,
};

function fmtAgo(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function fmtCountdown(iso) {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'imminent';
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const HEALTH_STYLES = {
  ok:        { tone: 'bg-emerald-50 text-emerald-700 border-emerald-200',  Icon: CheckCircle2, label: 'Ticking' },
  stale:     { tone: 'bg-amber-50 text-amber-800 border-amber-300',        Icon: AlertTriangle, label: 'Stale' },
  never_run: { tone: 'bg-slate-50 text-slate-700 border-slate-200',        Icon: Clock,         label: 'Never run' },
};

export default function CronHealthWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/academy/admin/cron/health`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const overall = useMemo(() => {
    if (!data) return null;
    if (data.stale_count > 0) return { tone: 'border-amber-300 bg-amber-50/40', label: `${data.stale_count} stale`, Icon: AlertTriangle, color: 'text-amber-700' };
    if (data.never_count > 0) return { tone: 'border-slate-200 bg-white',       label: `${data.never_count} never run yet`, Icon: Clock, color: 'text-slate-600' };
    return { tone: 'border-emerald-200 bg-white', label: `All ${data.ok_count} healthy`, Icon: CheckCircle2, color: 'text-emerald-600' };
  }, [data]);

  if (!loading && !data) return null;

  return (
    <section
      data-testid="cron-health-widget"
      className={`rounded-2xl border overflow-hidden transition-colors ${overall?.tone || 'border-gray-200 bg-white'}`}
    >
      <header className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${
            overall?.tone?.includes('emerald') ? 'bg-emerald-50 text-emerald-700' :
            overall?.tone?.includes('amber')   ? 'bg-amber-100 text-amber-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            <Activity className="h-5 w-5" />
          </span>
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Cron health</div>
            <div className="text-[15px] font-extrabold text-[#0e0e10] mt-0.5 flex items-center gap-2">
              {loading ? 'Checking automation jobs…' : (
                <>
                  {overall && <overall.Icon className={`h-4 w-4 ${overall.color}`} />}
                  {overall?.label}
                </>
              )}
            </div>
            {data && (
              <p className="text-[12px] text-[#6E6E73] mt-1">
                Daily at <span className="font-bold text-[#0e0e10]">{String(data.schedule.hour).padStart(2, '0')}:00 {data.schedule.tz}</span>
                {' · '}
                Next run in <span className="font-bold text-[#0e0e10]">{fmtCountdown(data.schedule.next_run)}</span>
                {' · '}
                <span className={data.config_flags?.teams_webhook ? 'text-emerald-700' : 'text-amber-700'}>
                  {data.config_flags?.teams_webhook ? 'Teams webhook live' : 'Teams webhook not configured'}
                </span>
                {!data.config_flags?.resend_key && <span className="text-amber-700"> · Resend key missing</span>}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="cron-health-refresh"
          className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Re-check
        </button>
      </header>

      {data && (
        <ul className="border-t border-gray-100 divide-y divide-gray-100 bg-white">
          {data.jobs.map((j) => {
            const Icon = ICON_BY_ID[j.id] || Activity;
            const style = HEALTH_STYLES[j.health] || HEALTH_STYLES.never_run;
            const last = j.last_run || {};
            const lastStatus = last.status || '—';
            return (
              <li key={j.id} data-testid={`cron-health-row-${j.id}`} className="px-5 py-3 grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 text-[12.5px]">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 text-gray-700 shrink-0">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <div className="font-bold text-[#0e0e10] truncate">{j.label}</div>
                  <div className="text-[11px] text-[#9aa0a6] truncate">{j.description}</div>
                </div>
                <div className="text-right hidden md:block">
                  <div className="text-[10.5px] uppercase tracking-wider text-[#9aa0a6]">Last run</div>
                  <div className="text-[12px] font-mono text-[#0e0e10]">{fmtAgo(last.ts)}</div>
                  <div className="text-[10.5px] text-[#9aa0a6] truncate max-w-[170px]">{lastStatus}</div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${style.tone}`}
                  title={`Health: ${j.health}`}
                >
                  <style.Icon className="h-3 w-3" />
                  {style.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {data && (
        <footer className="border-t border-gray-100 bg-[#FAFAFA] px-5 py-2.5 text-[11px] text-[#6E6E73] flex items-center justify-between gap-2 flex-wrap">
          <span>
            Image purge window: <strong className="text-[#0e0e10]">{data.config_flags?.image_purge_days}d</strong>
          </span>
          <a href="/admin?tab=catalog" className="inline-flex items-center gap-1 text-[#c9a227] font-bold uppercase tracking-wider hover:text-[#a8871a]">
            Open Catalog · Image Library <ChevronRight className="h-3 w-3" />
          </a>
        </footer>
      )}
    </section>
  );
}
